/**
 * Device information utilities for push device registration
 * Gathers device metadata for RegisterDeviceViewModel
 */

import { ENV } from '@/config/environment';
import { isBrowser } from './env';

/**
 * Get device type based on screen size and user agent
 * @returns Device type classification
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (!isBrowser()) return 'desktop';

  const width = window.innerWidth;
  const ua = navigator.userAgent.toLowerCase();

  // Mobile detection
  if (/mobile|iphone|ipod|android.*mobile/.test(ua) || width < 768) {
    return 'mobile';
  }

  // Tablet detection
  if (/tablet|ipad|android(?!.*mobile)/.test(ua) || (width >= 768 && width < 1024)) {
    return 'tablet';
  }

  return 'desktop';
}

/**
 * Get browser name and version from user agent
 * @returns Browser information object
 */
export function getBrowserInfo(): { name: string; version: string } {
  if (!isBrowser()) return { name: 'unknown', version: 'unknown' };

  const ua = navigator.userAgent;
  let name = 'unknown';
  let version = 'unknown';

  if (ua.indexOf('Firefox') > -1) {
    name = 'Firefox';
    version = ua.match(/Firefox\/([0-9.]+)/)?.[1] || 'unknown';
  } else if (ua.indexOf('Edg') > -1) {
    name = 'Edge';
    version = ua.match(/Edg\/([0-9.]+)/)?.[1] || 'unknown';
  } else if (ua.indexOf('Chrome') > -1) {
    name = 'Chrome';
    version = ua.match(/Chrome\/([0-9.]+)/)?.[1] || 'unknown';
  } else if (ua.indexOf('Safari') > -1) {
    name = 'Safari';
    version = ua.match(/Version\/([0-9.]+)/)?.[1] || 'unknown';
  }

  return { name, version };
}

/**
 * Get platform/OS name
 * @returns Platform name (e.g., "macOS", "Windows", "iOS", "Android")
 */
export function getPlatform(): string {
  if (!isBrowser()) return 'unknown';

  const ua = navigator.userAgent;

  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Windows/.test(ua)) return 'Windows';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Linux/.test(ua)) return 'Linux';

  return 'unknown';
}

/**
 * Get device name (browser + platform)
 * Example: "Chrome on macOS"
 * @returns Human-readable device name
 */
export function getDeviceName(): string {
  const browser = getBrowserInfo();
  const platform = getPlatform();
  return `${browser.name} ${browser.version} on ${platform}`;
}

/**
 * Get OS version from user agent
 * @returns OS version string or null if unavailable
 */
export function getOSVersion(): string | null {
  if (!isBrowser()) return null;

  const ua = navigator.userAgent;

  // iOS
  const iosMatch = ua.match(/OS (\d+_\d+(_\d+)?)/);
  if (iosMatch) {
    return iosMatch[1].replace(/_/g, '.');
  }

  // Android
  const androidMatch = ua.match(/Android (\d+(\.\d+)?)/);
  if (androidMatch) {
    return androidMatch[1];
  }

  // Windows
  const windowsMatch = ua.match(/Windows NT (\d+\.\d+)/);
  if (windowsMatch) {
    return `Windows NT ${windowsMatch[1]}`;
  }

  // macOS
  const macMatch = ua.match(/Mac OS X (\d+[._]\d+([._]\d+)?)/);
  if (macMatch) {
    return macMatch[1].replace(/_/g, '.');
  }

  return null;
}

/**
 * Get user timezone using Intl API
 * @returns IANA timezone string (e.g., "America/New_York")
 */
export function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Get preferred language from browser
 * @returns Language code (e.g., "en", "en-US")
 */
export function getPreferredLanguage(): string {
  if (!isBrowser()) return 'en';
  return navigator.language || 'en';
}

/**
 * Get app version from environment config
 * @returns Application version string
 */
export function getAppVersion(): string {
  return ENV.APP_VERSION || '1.0.0';
}
